import { AnimatePresence, motion } from "framer-motion";
import { Apple, Bot } from "lucide-react";
import { usePlatform } from "@/context/PlatformContext";
import { PLATFORM_IDS, PLATFORMS } from "@/data/platform";

/**
 * Premium Apple/Android toggle. One tap re-themes the entire site via
 * CSS variables + a short cross-fade on the layout. Use `variant="inline"`
 * for the strip version, or default for the hero-style pill.
 */
const PlatformSwitcher = ({ variant = "pill", className = "", testIdPrefix = "platform-switcher" }) => {
  const { platform, setPlatform } = usePlatform();

  const sizes =
    variant === "inline"
      ? { pad: "px-3.5 py-1.5", font: "text-[11px]", icon: "h-3 w-3", gap: "gap-1.5" }
      : { pad: "px-5 py-2.5", font: "text-xs", icon: "h-3.5 w-3.5", gap: "gap-2" };

  return (
    <div
      data-testid={testIdPrefix}
      className={`inline-flex items-center rounded-full border border-black/10 bg-white/80 p-1 shadow-subtle backdrop-blur-xl ${className}`}
    >
      {PLATFORM_IDS.map((id) => {
        const p = PLATFORMS[id];
        const isActive = platform === id;
        const Icon = id === "apple" ? Apple : Bot;
        return (
          <button
            key={id}
            type="button"
            data-testid={`${testIdPrefix}-${id}`}
            onClick={() => setPlatform(id)}
            aria-pressed={isActive}
            className={`relative inline-flex items-center ${sizes.gap} rounded-full ${sizes.pad} ${sizes.font} font-black uppercase tracking-[0.18em] transition-colors ${
              isActive ? "text-white" : "text-tracksy-ink/65 hover:text-tracksy-ink"
            }`}
          >
            {isActive && (
              <motion.span
                layoutId={`${testIdPrefix}-active`}
                className="absolute inset-0 rounded-full shadow-glow"
                style={{ background: p.accent }}
                transition={{ type: "spring", stiffness: 320, damping: 28 }}
              />
            )}
            <span className={`relative z-10 inline-flex items-center ${sizes.gap}`}>
              <Icon className={sizes.icon} aria-hidden />
              {p.short}
            </span>
          </button>
        );
      })}
      {/* Micro-caption that cycles with platform */}
      <AnimatePresence mode="wait">
        <motion.span
          key={platform}
          initial={{ opacity: 0, x: 4 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -4 }}
          transition={{ duration: 0.35 }}
          className="ml-2 hidden pr-2 text-[10px] font-black uppercase tracking-[0.22em] text-tracksy-sub sm:inline"
        >
          {PLATFORMS[platform].networkShort}
        </motion.span>
      </AnimatePresence>
    </div>
  );
};

export default PlatformSwitcher;
